import React, { useEffect, useRef } from "react";
import {
  I2cInput,
  I2cRadio,
  I2cRadioButton,
  I2cRadioGroup,
  I2cCheckboxGroup,
  I2cListItem,
  I2cCheckbox,
  I2cTooltip,
  I2cTextarea,
  I2cPhoneField,
  I2cIcon,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../Prepaid.scss";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import {
  corporateFundingSource, consumerFundingSource, kycResponsibility, keyProcess,  fields
} from "./stringUtils";
import { validationSchema } from "./validationSchema";
import CardOrderPlacementAndSecondaryCard from "./CardOrderPlacementAndSecondaryCard";
import CardActivationReloadingAndClosure from "./CardActivationReloadingAndClosure";
import CardNotificationAndSupport from "./CardNotificationAndSupport";

const ProgramFeatures = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => { 
  const generalErrorMessage = "Please fill out this field.";
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: {
      pf_enable_kyc_conduct: "",
      pf_enable_kyc_conduct_no_reason: "",
      pf_kyc_conduct: "",
      pf_kyc_conduct_other: "",
      pf_funding_source_consumer_option: [],
      pf_funding_source_corporate_option: "",
      pf_funding_source_corporate_other_company: "",
      pf_funding_source_corporate_other_contact_name: "",
      pf_funding_source_corporate_other_title: "",
      pf_funding_source_corporate_other_phone: "",
      pf_funding_source_corporate_other_phone_country: "",
      pf_funding_source_corporate_other_email: "",
      pf_funding_source_corporate_other_address: "",
      pf_card_placement: [],
      pf_card_placement_channel: [],
      pf_card_placement_channel_other: "",
      pf_supplimentary_cards_permitted: "",
      pf_supplimentary_cards_age_limit: "",
      supplimentary_cards_own_profile: "",
      supplimentary_cards_share_primary_balance: "",
      pf_card_activation_required: "",
      pf_card_activation_method: "",
      pf_card_activation_channels: [],
      pf_card_activation_channels_other: "",
      pf_card_offline_pin_required: "",
      pf_cardholder_notifications: "",
      pf_cardholder_notifications_channels: "",
      pf_customer_support_channels: [],
      pf_customer_support_provider: "",
      pf_customer_support_provider_other_company: "",
      pf_customer_support_provider_other_contact_name: "",
      pf_customer_support_provider_other_title: "",
      pf_customer_support_provider_other_phone: "",
      pf_customer_support_provider_other_phone_country: "",
      pf_customer_support_provider_other_email: "",
      pf_card_activation_parameters: [],
      pf_card_reloading_options: [],
      pf_card_reloading_options_other: "",
      pf_card_closure_how_will_card_be_closed: "",
      pf_card_closure_how_will_card_account_balance_be_treated_at_card_closure: ""
    },
  });

  // Controlls destructuring from the method
  const {
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        methods.setValue(field, formDataJson[1]["1_1"][field] ?? "");
      });
      methods.trigger();
    }
  }, [isFetching]);

  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
  }, []);

  // Helper function to format card placement for API backward compatibility
  // If single selection: return string; If multiple selections: return array
  const formatCardPlacementForAPI = (values) => {
    const placement = values.pf_card_placement;
    if (Array.isArray(placement) && placement.length === 1) {
      return placement[0]; // Return single string for backward compatibility
    }
    return placement; // Return array for multiple selections or empty array
  };

  const handleValueChanges = () => {
    const values = methods.getValues();
    const formattedValues = {
      ...values,
      pf_card_placement: formatCardPlacementForAPI(values)
    };
    setAddFormData({
      ...addFormData,
      1: {
        ...addFormData["1"],
        "1_1": { ...formattedValues },
      },
    });
  };

  // Update form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("1_1", true);
    else setFormValidStates("1_1", false);
    handleValueChanges();
  }, [Object.keys(errors).length]);

  const handleConsumerCheckboxGroupChange = (event) => {
    let selectedOptions;
    if (!event.length) selectedOptions = event.target.value;
    else selectedOptions = event;
    let updatedSelectedOptions = [
      ...(methods.getValues("pf_funding_source_consumer_option") ?? []),
    ];
    if (updatedSelectedOptions.includes(selectedOptions)) {
      // If the card is already selected, remove it
      updatedSelectedOptions = updatedSelectedOptions.filter(
        (card) => card !== selectedOptions,
      );
    } else {
      // If the card is not selected, add it
      updatedSelectedOptions.push(selectedOptions);
    }
    methods.setValue(
      "pf_funding_source_consumer_option",
      updatedSelectedOptions,
    );
    methods.trigger();
    handleValueChanges();
  };


  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-sm" className="section-space">
          <div i2c-grid="12">
            <div className="inner_step_title  mb-4">
              Card Holder Authentication
            </div>
            <div className="custom-label-light mb-16">
              Would you like to perform KYC for the card holder authentication?
            </div>
            <I2cRadioGroup
              onI2cChange={(e) => {
                if (e.target.value === "no" || e.target.value === "n/a") {
                  methods.setValue("pf_kyc_conduct", "");
                  methods.setValue("pf_kyc_conduct_other", "");
                }
                if (e.target.value !== "no") {
                  methods.setValue("pf_enable_kyc_conduct_no_reason", "");
                }
                methods.setValue("pf_enable_kyc_conduct", e.target.value);
                methods.trigger();
                handleValueChanges();
              }}
            >
              {keyProcess.map((radio, index) => (
                <I2cRadioButton
                  key={index}
                  checked={
                    methods.watch("pf_enable_kyc_conduct") ===
                    radio.toLocaleLowerCase()
                  }
                  value={radio.toLocaleLowerCase()}
                  label={radio}
                >
                  {radio}
                </I2cRadioButton>
              ))}
            </I2cRadioGroup>
          </div>
          {methods.watch("pf_enable_kyc_conduct") === "no" && (
            <div i2c-grid="12">
              <div i2c-grid="grid gap-sm" className="mt-12">
                <div i2c-grid="4@md">
                  <div className="single-field-wrapper">
                    <Controller
                      name="pf_enable_kyc_conduct_no_reason"
                      control={control}
                      render={({ field }) => (
                        <I2cInput
                          {...field}
                          value={field.value || ""}
                          className="single-input-field"
                          placeholder="Other Details"
                          onI2cInput={(e) => {
                            const value = e.target.value;
                            field.onChange(value);
                            methods.trigger();
                            handleValueChanges();
                          }}
                          invalid={errors.pf_enable_kyc_conduct_no_reason ? true : false}
                          size="large"
                          label="Enter Details"
                          name="pf_enable_kyc_conduct_no_reason"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {methods.watch("pf_enable_kyc_conduct") === "yes" && (
            <>
              <div i2c-grid="12">
                <div className="sub-heading mb-16">Who will conduct KYC?</div>
                <div className="single-field-wrapper">
                  <Controller
                    name="pf_kyc_conduct"
                    control={control}
                    render={({ field }) => (
                      <I2cRadioGroup
                        {...field}
                        className="centeredCheckbox"
                        onI2cChange={(e) => {
                          methods.setValue("pf_kyc_conduct", e.target.value);
                          if (e.target.value !== "other") {
                            methods.setValue("pf_kyc_conduct_other", "");
                          }
                          methods.trigger();
                          handleValueChanges();
                        }}
                      >
                        <div i2c-grid="grid gap-sm">
                          {kycResponsibility.map((card, index) => (
                            <I2cListItem
                              key={index}
                              i2c-grid="6@md"
                              variant="receipt"
                              label={card.label}
                              sublabel=""
                              value=""
                              type="checkbox"
                              checked={
                                methods.watch("pf_kyc_conduct") === card.value
                              }
                            >
                              <I2cRadio
                                slot="prefix"
                                filled
                                checked={
                                  methods.watch("pf_kyc_conduct") === card.value
                                }
                                value={card.value}
                              ></I2cRadio>
                            </I2cListItem>
                          ))}
                        </div>
                      </I2cRadioGroup>
                    )}
                  />
                  <ErrorMessage
                    show={
                      errors.pf_kyc_conduct &&
                        methods.formState.touchedFields.pf_kyc_conduct
                        ? true
                        : false
                    }
                    message={errors.pf_kyc_conduct?.message ?? "Invalid input"}
                  />
                </div>
              </div>
              {methods.watch("pf_kyc_conduct") === "other" && (
                <div key={"pf_kyc_conduct-other"} i2c-grid="12">
                  <div i2c-grid="grid gap-sm">
                    <div i2c-grid="4@md">
                      <div className="single-field-wrapper">
                        <Controller
                          name="pf_kyc_conduct_other"
                          control={control}
                          render={({ field }) => (
                            <I2cInput
                              {...field}
                              value={field.value || ""}
                              name="pf_kyc_conduct_other"
                              className="single-input-field"
                              placeholder="Please Provide Details"
                              onI2cInput={(e) => {
                                const value = e.target.value;
                                field.onChange(value);
                                methods.trigger();
                                handleValueChanges();
                              }}
                              invalid={errors.pf_kyc_conduct_other ? true : false}
                              size="large"
                              label="Enter Details"
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div i2c-grid="grid gap-md" className="section-space">
          <div i2c-grid="12">
            <div className="sub-heading mb-4">Funding Source for the Cards</div>
            <div className="custom-label-light">
              Select the relevant card funding options to confirm who will fund
              the cards and how they will be funded
            </div>
          </div>
          <div i2c-grid="12">
            <div className="custom-label-light mb-16">
              Loaded by the consumer through
            </div>
            <div className="single-field-wrapper">
              <Controller
                name="pf_funding_source_consumer_option"
                control={control}
                render={({ field }) => (
                  <I2cCheckboxGroup
                    {...field}
                    className="centeredCheckbox"
                    label=""
                  >
                    <div i2c-grid="grid gap-sm">
                      {consumerFundingSource.map((card, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="6@md"
                          variant="receipt"
                          label={card.label}
                          sublabel=""
                          value=""
                          type="checkbox"
                          checked={
                            methods
                              .watch("pf_funding_source_consumer_option")
                              ?.includes(card.value) ?? false
                          }
                          onI2cCheckboxClicked={(e) => {
                            handleConsumerCheckboxGroupChange(card.value);
                          }}
                        >
                          <I2cCheckbox
                            slot="prefix"
                            filled
                            value={card.value}
                            checked={
                              methods
                                .watch("pf_funding_source_consumer_option")
                                ?.includes(card.value) ?? false
                            }
                          // onI2cChange={(e) => {
                          //   handleConsumerCheckboxGroupChange(e);
                          // }}

                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                )}
              />
              <ErrorMessage
                show={
                  errors.pf_funding_source_consumer_option &&
                    methods.formState.touchedFields
                      .pf_funding_source_consumer_option
                    ? true
                    : false
                }
                message={
                  errors.pf_funding_source_consumer_option?.message ??
                  "Invalid input"
                }
              />
            </div>
          </div>
          <div i2c-grid="12">
            <div className="single-field-wrapper">
              <Controller
                name="pf_funding_source_corporate_option"
                control={control}
                render={({ field }) => (
                  <I2cRadioGroup
                    {...field}
                    className="centeredCheckbox"
                    label="Loaded by a corporate entity through"
                    onI2cChange={(e) => {
                      if (e.target.value !== "other") {
                        const arr = [
                          "pf_funding_source_corporate_other_company",
                          "pf_funding_source_corporate_other_contact_name",
                          "pf_funding_source_corporate_other_title",
                          "pf_funding_source_corporate_other_phone",
                          "pf_funding_source_corporate_other_phone_country",
                          "pf_funding_source_corporate_other_email",
                          "pf_funding_source_corporate_other_address",
                        ];
                        arr.forEach((field) => methods.setValue(field, ""));
                      }
                      methods.setValue(
                        "pf_funding_source_corporate_option",
                        e.target.value,
                      );
                      methods.trigger();
                      handleValueChanges();
                    }}
                  >
                    <div i2c-grid="grid gap-sm">
                      {corporateFundingSource.map((card, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="6@md"
                          variant="receipt"
                          label={card.label}
                          sublabel=""
                          value=""
                          type="checkbox"
                          checked={
                            methods
                              .watch("pf_funding_source_corporate_option")
                              ?.includes(card.value) ?? false
                          }
                        >
                          <I2cRadio
                            slot="prefix"
                            filled
                            checked={
                              methods.watch(
                                "pf_funding_source_corporate_option",
                              ) === card.value
                            }
                            value={card.value}
                          ></I2cRadio>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cRadioGroup>
                )}
              />
              <ErrorMessage
                show={
                  errors.pf_funding_source_corporate_option &&
                    methods.formState.touchedFields
                      .pf_funding_source_corporate_option
                    ? true
                    : false
                }
                message={
                  errors.pf_funding_source_corporate_option?.message ??
                  "Invalid input"
                }
              />
            </div>
            {methods.watch("pf_funding_source_corporate_option") ===
              "other" && (
                <>
                  <div className="mt-16">
                    <div className="custom-label-light mb-12">
                      If using a 3rd Party Vendor, please provide details
                    </div>
                  </div>
                  <div i2c-grid="12">
                    <div i2c-grid="grid gap-column-sm gap-row-md">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <Controller
                            name="pf_funding_source_corporate_other_company"
                            control={control}
                            render={({ field }) => (
                              <I2cInput
                                {...field}
                                name={"pf_funding_source_corporate_other_company"}
                                className="single-input-field"
                                placeholder="Enter Company Name"
                                onI2cInput={(e) => {
                                  const value = e.target.value;
                                  field.onChange(value);
                                  methods.trigger();
                                  handleValueChanges();
                                  if (value.length === 0) {
                                    methods.setError(
                                      "pf_funding_source_corporate_other_company",
                                      {
                                        message: generalErrorMessage,
                                      },
                                    );
                                  } else {
                                  }
                                }}
                                required={
                                  errors.pf_funding_source_corporate_other_company
                                    ? true
                                    : false
                                }
                                size="large"
                                label="Company Name"
                              />
                            )}
                          />

                        </div>
                      </div>
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <Controller
                            name="pf_funding_source_corporate_other_contact_name"
                            control={control}
                            render={({ field }) => (
                              <I2cInput
                                {...field}
                                name={
                                  "pf_funding_source_corporate_other_contact_name"
                                }
                                className="single-input-field"
                                placeholder="Enter Contact Name"
                                onI2cInput={(e) => {
                                  const value = e.target.value;
                                  field.onChange(value);
                                  methods.trigger();
                                  handleValueChanges();
                                  if (value.length === 0) {
                                    methods.setError(
                                      "pf_funding_source_corporate_other_contact_name",
                                      {
                                        message: generalErrorMessage,
                                      },
                                    );
                                  } else {
                                  }
                                }}
                                required={
                                  errors.pf_funding_source_corporate_other_contact_name
                                    ? true
                                    : false
                                }
                                size="large"
                                label="Contact Name"
                              />
                            )}
                          />

                        </div>
                      </div>
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <Controller
                            name="pf_funding_source_corporate_other_title"
                            control={control}
                            render={({ field }) => (
                              <I2cInput
                                {...field}
                                name={"pf_funding_source_corporate_other_title"}
                                className="single-input-field"
                                placeholder="Enter Title"
                                onI2cInput={(e) => {
                                  const value = e.target.value;
                                  field.onChange(value);
                                  methods.trigger();
                                  handleValueChanges();
                                  if (value.length === 0) {
                                    methods.setError(
                                      "pf_funding_source_corporate_other_title",
                                      {
                                        message: generalErrorMessage,
                                      },
                                    );
                                  } else {
                                  }
                                }}
                                required={
                                  errors.pf_funding_source_corporate_other_title
                                    ? true
                                    : false
                                }
                                size="large"
                                label="Title"
                              />
                            )}
                          />

                        </div>
                      </div>
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cPhoneField
                            format="NATIONAL"
                            label="Cell Number"
                            helpIcon=""
                            name="pf_funding_source_corporate_other_phone"
                            size="large"
                            country-box
                            show-flags
                            value={methods.watch(
                              "pf_funding_source_corporate_other_phone",
                            )}
                            selectedCountry={
                              methods.watch("pf_funding_source_corporate_other_phone_country") || "US"
                            }
                            errorMessageVisible={false}
                            onI2cChange={(e) => {
                              const country = e.target.country;
                              const isValid = e.target.isValid;
                              const value = e.target.value;
                              methods.setValue(
                                "pf_funding_source_corporate_other_phone_country",
                                country,
                              );
                              methods.setValue(
                                "pf_funding_source_corporate_other_phone",
                                value,
                              );
                              methods.trigger();
                              handleValueChanges();
                              if (!isValid) {
                                methods.setError(
                                  "pf_funding_source_corporate_other_phone",
                                  {
                                    message: e.target.guide,
                                  },
                                );
                              } else {
                                methods.clearErrors(
                                  "pf_funding_source_corporate_other_phone",
                                );
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <Controller
                            name="pf_funding_source_corporate_other_email"
                            control={control}
                            render={({ field }) => (
                              <I2cInput
                                {...field}
                                name={"pf_funding_source_corporate_other_email"}
                                className="single-input-field"
                                placeholder="Enter Email"
                                onI2cInput={(e) => {
                                  const value = e.target.value;
                                  field.onChange(value);
                                  methods.trigger();
                                  handleValueChanges();
                                  if (value.length === 0) {
                                    methods.setError(
                                      "pf_funding_source_corporate_other_email",
                                      {
                                        message: generalErrorMessage,
                                      },
                                    );
                                  } else {
                                  }
                                }}
                                required={
                                  errors.pf_funding_source_corporate_other_email
                                    ? true
                                    : false
                                }
                                size="large"
                                label="Email"
                                type="email"
                              />
                            )}
                          />

                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
          </div>
        </div>
        <CardOrderPlacementAndSecondaryCard control={control}
          handleValueChanges={handleValueChanges}
          methods={methods}
          errors={errors} />
        <CardActivationReloadingAndClosure control={control}
          handleValueChanges={handleValueChanges}
          methods={methods}
          errors={errors} />
        <CardNotificationAndSupport control={control}
          handleValueChanges={handleValueChanges}
          methods={methods}
          errors={errors} />
      </div>
    </React.Fragment>
  );
};

export default ProgramFeatures;
